import React, { useRef, useState, useEffect } from "react";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { vendorVerifyOTP } from "../../redux/slices/vendorAuthSlice";
import { vendorSendOTP } from "../../redux/slices/vendorAuthSlice";
import { setIsRegistered } from "../../redux/slices/vendorRegisterSlice";

const VendorPortalOTP = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { phone, isOTPSent, loading, doesVendorPartnerExists } = useSelector(
    (state) => state.vendorAuth
  );
  const [timer, setTimer] = useState(60);

  const [inputs, setInputs] = useState(Array(4).fill(""));
  const inputRefs = useRef([]);
  //   const [loading, setLoading] = useState(false);
  const [isValidOtp, setIsValidOtp] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    if (!isOTPSent) {
      navigate("/vendorportal");
    }
    inputRefs.current[0]?.focus();
    setTimer(60);
  }, []);

  useEffect(() => {
    if (timer === 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return;

    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    setIsValidOtp(true);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !inputs[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otp = inputs.join("");

    if (otp.length !== 4) {
      setIsValidOtp(false);
      setSnackbar({
        open: true,
        message: "Please enter a valid 4-digit OTP",
        severity: "error",
      });
      return;
    }

    // setLoading(true);
    try {
      const resultAction = await dispatch(vendorVerifyOTP({ phone, otp }));
      if (vendorVerifyOTP.fulfilled.match(resultAction)) {
        setSnackbar({
          open: true,
          message: "OTP Verified!",
          severity: "success",
        });
        const vendorPartnerExistsFromResponse =
          resultAction.payload.doesVendorPartnerExists;
        dispatch(setIsRegistered(vendorPartnerExistsFromResponse));
        setTimeout(() => {
          if (vendorPartnerExistsFromResponse) {
            navigate("/vendorportal/dashboard");
          } else {
            navigate("/vendorportal/register");
          }
        }, 1000);
      } else {
        throw new Error(resultAction.data.message || "Invalid OTP");
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to verify OTP",
        severity: "error",
      });
      setIsValidOtp(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await dispatch(vendorSendOTP(phone));

      if (vendorSendOTP.fulfilled.match(response)) {
        setSnackbar({
          open: true,
          message: "OTP resent successfully",
          severity: "success",
        });
      } else {
        throw new Error("Failed to resend OTP");
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to resend OTP",
        severity: "error",
      });
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Image */}
      <div className="w-1/2 hidden md:block overflow-hidden">
        <img
          src="/assests/vendorPortalImages/vendorLogin.png"
          alt="Background"
          className="h-screen p-4 rounded-[65px] w-full object-cover"
        />
      </div>

      {/* OTP Section */}
      <div className="mx-auto flex justify-center items-center pr-2">
        <div className="mx-auto flex justify-center items-center">
          <div>
            <div className=" flex justify-between gap-8 items-center">
              <h1 className="text-3xl font-bold mb-4">Verify Phone Number</h1>
              {!doesVendorPartnerExists && (
                <button
                  className=" bg-blue-100 text-md font-semibold px-3 py-1 rounded-full border-2 "
                  style={{
                    color: "var(--primary-color)",
                    border: " 2px solid var(--primary-color)",
                  }}
                >
                  New User
                </button>
              )}
            </div>
            <p className="mb-6">
              Please enter your verification code sent on <br />
              <span className="font-bold">{phone}</span>
            </p>

            {/* OTP Inputs */}
            <div className="flex justify-center gap-6 mb-6">
              {[...Array(4)].map((_, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={inputs[index]}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  className={`w-12 h-12 mx-1 border ${
                    isValidOtp ? "border-blue-300" : "border-red-500"
                  } rounded-lg text-center text-lg font-semibold focus:outline-none focus:ring-2 ${
                    isValidOtp ? "focus:ring-blue-500" : "focus:ring-red-500"
                  }`}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={handleVerifyOTP}
              disabled={loading}
              className="w-full flex items-center justify-between font-semibold py-2 px-4 rounded-lg mb-4"
              style={{
                backgroundColor: "var(--primary-color)",
                color: "var(--secondary-color)",
              }}
            >
              {/* Left spacer to keep text centered when loader shows */}
              <span className="w-6" />

              {/* Center text */}
              <span>{loading ? "Wait..." : "Verify"}</span>

              {/* Right loader or empty space to balance layout */}
              <span className="w-6 flex justify-end">
                {loading && (
                  <CircularProgress
                    size={20}
                    style={{ color: "var(--secondary-color)" }}
                  />
                )}
              </span>
            </button>


            {/* Resend */}
            <p className="text-start mt-6 text-sm">
              Didn’t receive OTP?{" "}
              <span
                onClick={timer === 0 ? handleResendOTP : null}
                className={`cursor-pointer font-medium ${
                  timer === 0
                    ? "text-blue-600"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              >
                {timer === 0 ? "Resend OTP" : `Resend OTP in ${timer}s`}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            backgroundColor:
              snackbar.severity === "success"
                ? "var(--third-color)"
                : "var(--heading-color)",
            color: "var(--secondary-color)",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default VendorPortalOTP;
